"use client";

import React, { FC, useEffect, useRef, useState } from "react";
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { getCart } from "@/app/lib/reducers/cartReducer";
import CartModal from "./CartModal";

/**
 * Cart icon button for the topbar. Renders the cart count badge and opens
 * the CartModal on click. Replaces the legacy CartDropdown tooltip — the
 * cart fetch debouncing logic (one call per user+language) is reused here
 * so we don't double-hit the slow /api/cart endpoint when navigating.
 */
const CartTrigger: FC<{ className?: string }> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);

  const cartItems = useAppSelector((s) => s.cart.items) || [];
  const count = cartItems.length;
  const selectedLanguage = useAppSelector(
    (s) => s.language.selectedLanguage
  );
  const connectedUser = useAppSelector((s) => s.auth.user);

  const userUuid = connectedUser?.uuid;
  const lastFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!userUuid || !selectedLanguage) return;
    const key = `${userUuid}::${selectedLanguage}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    dispatch(getCart(selectedLanguage));
  }, [dispatch, selectedLanguage, userUuid]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open cart"
        className={`relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors ${className}`}
      >
        <ShoppingCartIcon className="w-6 h-6" />
        {count > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C13B5C] text-white text-[10px] font-bold flex items-center justify-center">
            {count}
          </span>
        )}
      </button>
      <CartModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default CartTrigger;
